import { useStyle } from '@/classify';
import { useWindowSize } from '@/common/hooks/useWindowSize';
import ProductSort from '@/components/ProductSort';
import { useFilterModal } from '@/hooks/FilterModal/useFilterModal';
import { cn } from '@/lib/utils';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo, useRef } from 'react';
import { FocusScope } from 'react-aria';
import { ChevronDown, ChevronUp, X as CloseIcon } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { Accordion, Section } from '../Accordion';
import Icon from '../Icon';
import LinkButton from '../LinkButton';
import { Portal } from '../Portal';
import CurrentFilters from './CurrentFilters';
import FilterBlock from './filterBlock';
import blockClasses from './filterBlock.module.css';
import FilterFooter from './filterFooter';
import defaultClasses from './filterModal.module.css';
/**
 * A view that displays applicable and applied filters.
 *
 * @param {Object} props.filters - filters to display
 */
const FilterModal = props => {
  const {
    categoryId,
    filters,
    categoryName,
    totalCount,
    aggregations,
    currentCurrencyCode,
    sortFields,
    categoryUrlKey,
    handleClose,
    handleOpen,
    isOpen,
    shouldShowFilterButtons,
    availableSortMethods,
    sortState,
    sortApi,
    handleGenerateLinkHref,
  } = props;
  const { formatMessage } = useIntl();

  const filterRef = useRef();

  const talonProps = useFilterModal({
    categoryId,
    filters,
    aggregations,
    sortFields,
    categoryUrlKey,
    handleClose,
    handleOpen,
    isOpen,
  });

  const {
    filterApi,
    filterItems,
    filterNames,
    filterFrontendInput,
    filterState,
    handleReset,
    handleApply,
    handleKeyDownActions,
    filterCount,
    loadingCount,
  } = talonProps;

  const pathname = usePathname();

  const classes = useStyle(defaultClasses, props.classes);
  const modalClass = isOpen ? classes.root_open : classes.root;

  const handleApplyFilter = useCallback(
    (...args) => {
      handleApply(...args);
    },
    [handleApply, filterRef],
  );

  const filtersList = useMemo(
    () =>
      Array.from(filterItems, ([group, items]) => {
        const blockState = filterState.get(group);
        const groupName = filterNames.get(group);
        const frontendInput = filterFrontendInput.get(group);

        // 移动端
        if (groupName === 'Brand' || groupName === 'Price') {
          return null;
        }

        // if (group === 'lw_size_shoes_adult') {
        //   console.log(blockState);
        // }

        return (
          <FilterBlock
            key={group}
            filterApi={filterApi}
            filterState={blockState}
            filterFrontendInput={frontendInput}
            group={group}
            items={items}
            name={groupName}
            currentCurrencyCode={currentCurrencyCode}
            handleGenerateLinkHref={handleGenerateLinkHref}
          />
        );
      }),
    [
      filterItems,
      filterState,
      filterNames,
      filterFrontendInput,
      filterApi,
      currentCurrencyCode,
      handleGenerateLinkHref,
    ],
  );
  const windowSize = useWindowSize();

  const filtersAriaLabel = formatMessage({
    id: 'filterModal.filters.ariaLabel',
    defaultMessage: 'Filters',
  });

  const closeAriaLabel = formatMessage({
    id: 'filterModal.filters.close.ariaLabel',
    defaultMessage: 'Close filters popup.',
  });

  const clearAllAriaLabel = formatMessage({
    id: 'filterModal.action.clearAll.ariaLabel',
    defaultMessage: 'Clear all applied filters',
  });

  const clearAll = (
    <div className={classes.action}>
      <LinkButton
        type='button'
        onClick={() => {
          if (filterState?.size) {
            handleReset();
            handleClose();
          }
        }}
        ariaLabel={clearAllAriaLabel}
      >
        <FormattedMessage
          id={'filterModal.action'}
          defaultMessage={'Clear all'}
        />
      </LinkButton>
    </div>
  );

  if (!shouldShowFilterButtons) {
    return null;
  }

  if (!isOpen) {
    return null;
  }
  const isMobileAndTablet = windowSize.innerWidth <= 960;
  const AccordionClassName = 'border-b border-[#ccc]';

  return (
    <Portal>
      {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
      <FocusScope
        contain
        restoreFocus
        autoFocus
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <aside
          className={modalClass}
          onKeyDown={handleKeyDownActions}
        >
          <div className={cn(classes.body, 'border-[#E0E0E0]')}>
            <div className={classes.filterBanner}>
              {/* <div className={classes.filterContent}> */}
              <span className={classes.filteTitle}>{categoryName || ''}</span>
              {/* {totalCount !== null && totalCount !== undefined && (
                  <span className={classes.filterText}>({totalCount} Products)</span>
                )} */}
              {/* </div> */}
              <Icon
                className={classes.modalCloseBtn}
                src={CloseIcon}
                size={26}
                onClick={handleClose}
              />
            </div>
            <CurrentFilters
              filterApi={filterApi}
              filterNames={filterNames}
              filterState={filterState}
              onRemove={handleApplyFilter}
              handleReset={handleReset}
              clearAll={handleReset}
              isResearch={false}
              isOpenSeeMore={false}
              classes={{
                resetFiltersWrapper: 'px-[12px]',
              }}
            />
            <ul
              className={classes.blocks}
              aria-label={filtersAriaLabel}
            >
              {isMobileAndTablet && (
                <Accordion
                  canOpenMultiple
                  classes={{
                    root: AccordionClassName,
                  }}
                >
                  <Section
                    customOpenIcon={ChevronUp}
                    customCloseIcon={ChevronDown}
                    className={blockClasses.section}
                    classes={{
                      title: blockClasses.accordionTitle,
                      title_container: blockClasses.accordionTitleContainer,
                      contents_container: blockClasses.accordionContentsContainer2,
                    }}
                    title={'Sort By'}
                  >
                    <ProductSort
                      sortState={sortState}
                      sortApi={sortApi}
                      availableSortMethods={availableSortMethods}
                    />
                  </Section>
                </Accordion>
              )}
              {filtersList}
            </ul>
          </div>
          <FilterFooter
            applyFilters={handleApply}
            handleClose={handleClose}
            hasFilters={!!filterState.size}
            isOpen={isOpen}
            clearAll={clearAll}
            handleReset={handleReset}
            totalCount={filterCount}
            loadingCount={loadingCount}
          />
        </aside>
      </FocusScope>
    </Portal>
  );
};

export default FilterModal;
